// profileUtils.js - Utilities for managing user profile data

// Generate a simple user ID or get from storage
// In a real app, you'd get this from your auth system
export const getUserId = () => {
  let userId = localStorage.getItem('userId');
  if (!userId) {
    userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('userId', userId);
  }
  return userId;
};

/**
 * Get user profile from DynamoDB and localStorage
 * @param {Object} options - Options for loading profile
 * @param {string} options.userId - Optional user ID to use instead of stored one
 * @returns {Promise<Object>} - The user profile data
 */
export const getUserProfile = async (options = {}, dynamoUtilsRef = null) => {
  const userId = options.userId || getUserId();
  let profile = null;
  
  // Check localStorage first for immediate use
  const localProfile = localStorage.getItem('userProfile');
  if (localProfile) {
    try {
      profile = JSON.parse(localProfile);
    } catch (e) {
      console.error('Error parsing local profile:', e);
    }
  }
  
  // Try to load from DynamoDB if utils are available
  const utils = dynamoUtilsRef?.current;
  if (utils) {
    try {
      console.log('Attempting to load profile from DynamoDB for user:', userId);
      const dynamoProfile = await utils.getUserProfile(userId);
      
      if (dynamoProfile) {
        profile = dynamoProfile;
        console.log('Loaded user profile from DynamoDB');
      }
    } catch (dbError) {
      console.log('Error loading profile from DynamoDB:', dbError);
      // Not showing error since we might still have localStorage profile
    }
  }
  
  return profile;
};

/**
 * Save user profile to DynamoDB and localStorage
 * @param {Object} profileData - The profile data to save
 * @param {string} userId - The authenticated user ID (user.sub) - REQUIRED
 * @returns {Promise<boolean>} - Whether the operation was successful
 */
export const saveUserProfile = async (profileData, userId = null, dynamoUtilsRef = null) => {
  if (!profileData) return false;
  
  // Require authenticated userId - no fallback to generated ID
  if (!userId) {
    console.error('Cannot save user profile - authenticated userId is required');
    return false;
  }
  
  // Always save to localStorage for offline access
  try {
    localStorage.setItem('userProfile', JSON.stringify(profileData));
  } catch (e) {
    console.error('Error saving profile to localStorage:', e);
  }
  
  // If DynamoDB utils are available, save there too
  if (dynamoUtilsRef?.current) {
    try {
      const success = await dynamoUtilsRef.current.saveUserProfile(profileData, userId);
      return success;
    } catch (dbError) {
      console.error('Error saving profile to DynamoDB:', dbError);
      // Return false to indicate failure - don't mask the error
      return false;
    }
  } else {
    console.error('No DynamoDB utils reference available - cannot save profile to database');
    return false;
  }
};